/**
 * XSS Koruması - Güvenlik fonksiyonları
 */

/**
 * HTML karakterlerini escape et (XSS koruması)
 * @param {string} text - Temizlenecek metin
 * @returns {string} Temizlenmiş metin
 */
export const escapeHtml = (text) => {
  if (!text) return '';
  
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  
  return String(text).replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Script tag'lerini temizle
 * @param {string} text - Temizlenecek metin
 * @returns {string} Temizlenmiş metin
 */
export const stripScripts = (text) => {
  if (!text) return '';
  
  return String(text)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, ''); // onclick, onerror, vb.
};

/**
 * Güvenli input değeri - Hem escape hem strip
 * @param {string} text - Temizlenecek metin
 * @returns {string} Temizlenmiş metin
 */
export const sanitizeInput = (text) => {
  if (!text) return '';
  
  return stripScripts(escapeHtml(text));
};

/**
 * Sadece belirli karakterlere izin ver (ad, soyad için)
 * @param {string} text - Temizlenecek metin
 * @returns {string} Temizlenmiş metin
 */
export const sanitizeName = (text) => {
  if (!text) return '';
  
  // Sadece harf, boşluk, Türkçe karakterler
  return String(text)
    .replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, '')
    .trim()
    .substring(0, 50); // Max 50 karakter
};

/**
 * Telefon numarasını temizle
 * @param {string} phone - Telefon numarası
 * @returns {string} Temizlenmiş telefon
 */
export const sanitizePhone = (phone) => {
  if (!phone) return '';
  
  // Sadece rakam, +, (, ), -, boşluk
  return String(phone)
    .replace(/[^0-9+\-() ]/g, '')
    .trim()
    .substring(0, 20);
};

/**
 * Email'i doğrula ve temizle
 * @param {string} email - Email adresi
 * @returns {string} Temizlenmiş email
 */
export const sanitizeEmail = (email) => {
  if (!email) return '';
  
  const cleaned = String(email)
    .toLowerCase()
    .trim()
    .substring(0, 100);
  
  // Basit email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(cleaned) ? cleaned : '';
};

/**
 * URL'i doğrula ve temizle
 * @param {string} url - URL
 * @returns {string} Temizlenmiş URL veya boş string
 */
export const sanitizeUrl = (url) => {
  if (!url) return '';
  
  try {
    const parsed = new URL(url);
    // Sadece http ve https'e izin ver
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
  } catch (e) {
    // Geçersiz URL
  }
  
  return '';
};

/**
 * Form verilerini toplu temizle
 * @param {Object} formData - Form verileri
 * @param {Object} rules - Her alan için temizleme kuralları
 * @returns {Object} Temizlenmiş form verileri
 */
export const sanitizeFormData = (formData, rules = {}) => {
  const sanitized = {};
  
  for (const [key, value] of Object.entries(formData)) {
    const rule = rules[key] || 'input';
    
    switch (rule) {
      case 'name':
        sanitized[key] = sanitizeName(value);
        break;
      case 'email':
        sanitized[key] = sanitizeEmail(value);
        break;
      case 'phone':
        sanitized[key] = sanitizePhone(value);
        break;
      case 'url':
        sanitized[key] = sanitizeUrl(value);
        break;
      case 'input':
      default:
        sanitized[key] = sanitizeInput(value);
        break;
    }
  }
  
  return sanitized;
};

/**
 * HTML string'i güvenli bir şekilde render etmek için
 * dangerouslySetInnerHTML ile kullanılabilir
 * 
 * Kullanım:
 * <div dangerouslySetInnerHTML={createSafeMarkup(userInput)} />
 * 
 * @param {string} html - Temizlenecek HTML
 * @returns {Object} React dangerouslySetInnerHTML objesi
 */
export const createSafeMarkup = (html) => {
  return { __html: escapeHtml(html) };
};
